import { missingOrInvalidField } from "../../utils/Enquiries";
import { pushGa4Event } from "./ga4_helper";

const toSnakeCase = string => string.replace(/([A-Z]+)/g, "_$1").replace(/^_/, "");

const VIEWS = {
  PERSONAL_DETAILS: ["Enquiry, Personal Details", "/enquiries/personal_details"],
  TRAVEL_DETAILS: ["Enquiry, Travel Details", "/enquiries/travel_details"],
  ENQUIRY_COMPLETE: ["Enquiry, Completed", "/enquiries/completed"],
  BOOKING_CUSTOMIZE_OFFER: ["Customize Offer", "/booking/customize-offer"],
  BOOKING_PERSONAL_DETAILS: ["Personal Details", "/booking/personal-details"],
  BOOKING_STRIPE_CHECKOUT: ["Stripe Checkout", "/booking/checkout"],
  BOOKING_COMPLETE: ["Booking Complete", "/booking/complete"]
};

const DEFAULT_CONSENT = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied'
};

const EVENT_TYPES = {
  DYNAMIC_REMARKETING: 'dynamicRemarketing',
  GA: 'GAEvent',
  GA_NO_HIT: 'GAEventNoHit',
  GA4: 'GA4Event',
  GA4_NO_HIT: 'GA4EventNoHit',
  VIRTUAL_PAGEVIEW: 'VirtualPageview'
};

const GOALS = {
  CLICKED_PACKAGE: ["Search Page", "Clicked Package"],
  CLICKED_DYNAMIC_PACKAGE: ["Dynamic Page", "Clicked Package"],
  VIEWED_DYNAMIC: ["Dynamic Page View", "Success"],
  VIEWED_REGION: ["Region View", "Success"],
  VIEWED_VENUE: ["Venue View", "Success"]
};

const COMMON_METRICS = ["goal", "ecommerce", "remarketing"];

const hasMarketingConsent = () => {
  return window.Cookiebot && window.Cookiebot.consent && window.Cookiebot.consent.marketing;
};

let lastVisibilityChange;

const pushDataLayer = payload => {
  if (!payload) {
    return;
  }

  window.dataLayer.push(payload);
};

const pushGAEvent = (event, payload) => {
  if (!payload.eventCategory || !payload.eventAction) {
    return;
  }

  pushDataLayer({ event, ...payload });
};

const dynamicRemarketing = (remarketingData) => {
  if (hasMarketingConsent()) {
    pushDataLayer({ event: EVENT_TYPES.DYNAMIC_REMARKETING, remarketingData });
  }
};

const trackCommonMetrics = (data) => {
  const commonMetrics = hasMarketingConsent()
    ? COMMON_METRICS
    : COMMON_METRICS.filter(metric => metric !== "remarketing");

  commonMetrics.forEach(metric => pushDataLayer(data[metric]));
};

const gaEvent = (eventCategory, eventAction, eventLabel) => {
  pushGAEvent(EVENT_TYPES.GA, { eventCategory, eventAction, eventLabel });
  pushGAEvent(EVENT_TYPES.GA4, { eventCategory, eventAction, eventLabel });
};

const gaEventNoHit = (eventCategory, eventAction, eventLabel) => {
  pushGAEvent(EVENT_TYPES.GA_NO_HIT, { eventCategory, eventAction, eventLabel });
  pushGAEvent(EVENT_TYPES.GA4_NO_HIT, { eventCategory, eventAction, eventLabel });
};

const ga4Event = (eventCategory, eventAction, eventLabel) => {
  pushGAEvent(EVENT_TYPES.GA4, { eventCategory, eventAction, eventLabel });
};

const virtualPageView = pageView => {
  const view = VIEWS[pageView] || VIEWS[toSnakeCase(pageView).toUpperCase()];
  const event = EVENT_TYPES.VIRTUAL_PAGEVIEW;

  if (!view) {
    throw new ReferenceError(`${event}: "${pageView}" not found`);
  }

  const [virtualPageTitle, virtualPageURL] = view;

  pushDataLayer({ event, virtualPageTitle, virtualPageURL });
};

const trackGoal = goalName => {
  const goal = GOALS[goalName];

  if (!goal) {
    return;
  }

  const [action, label] = goal;

  gaEvent("Goals", action, label);
};

const trackEnquiry = (gtmData, leadValue) => {
  trackCommonMetrics(gtmData);
  virtualPageView("ENQUIRY_COMPLETE");
  pushGa4Event({
    event: "generate_lead",
    lead_type: "Enquiry",
    lead_value: leadValue
  })
};

const trackLastMinuteDeal = data => {
  trackCommonMetrics(data);
  virtualPageView("BOOKING_COMPLETE");
};

const trackClick = target => {
  const { gtmCategory, gtmAction, gtmLabel, gtmPageview } = target.dataset;

  if (gtmCategory) {
    gaEvent(gtmCategory, gtmAction, gtmLabel || target.href);
  }

  if (gtmPageview) {
    virtualPageView(gtmPageview);
  }
};

const trackInvalidEnquiryForm = target => {
  const formType = target.form.dataset.gtmFormType || "Enquiry Form";

  gaEventNoHit("Failed Enquiry", formType, missingOrInvalidField(target));
};

const trackServerSideEnquiryError = target => {
  const trackedError = "Failed enquiry: 422 error";
  const formType = target.dataset.gtmFormType || "Enquiry Form";

  gaEventNoHit("Failed Enquiry", formType, trackedError);
};

const trackVisibilityState = () => {
  const now = new Date().getTime();
  const { dataLayer } = window;
  let state = document.visibilityState;

  if (!lastVisibilityChange) {
    const GTM_START = "gtm.start";
    const start = dataLayer.find(entry => entry && entry[GTM_START]);

    lastVisibilityChange = start ? start[GTM_START] : now - 1;
  } else {
    state = state === "hidden" ? "visible" : "hidden";
  }

  const diff = now - lastVisibilityChange;

  if (diff) {
    pushGAEvent(EVENT_TYPES.GA_NO_HIT, {
      eventCategory: "Page Visibility",
      eventAction: state,
      eventLabel: location.pathname,
      eventValue: diff
    });
  }

  lastVisibilityChange = now;
};

const trackTotalVisibility = (visible) => {
  const eventAction = visible ? "visible" : "hidden";
  const eventCategory = `Total Page ${visible ? "Visibility" : "Invisibility"}`;
  const total = window.dataLayer
    .filter(Boolean)
    .filter(entry => entry.eventCategory === "Page Visibility" && entry.eventAction === eventAction)
    .reduce((acc, entry) => acc + entry.eventValue, 0);

  pushGAEvent(EVENT_TYPES.GA_NO_HIT, {
    eventCategory,
    eventAction: "Page Unload",
    eventLabel: location.pathname,
    eventValue: Math.min(total, 30 * 60 * 1e3)
  });
};

window.dataLayer = window.dataLayer || [];

window.addEventListener("load", trackVisibilityState);
window.addEventListener("beforeunload", () => {
  trackVisibilityState();
  trackTotalVisibility(true);
  trackTotalVisibility();
});

document.addEventListener("visibilitychange", trackVisibilityState);

export {
  dynamicRemarketing,
  gaEvent,
  gaEventNoHit,
  ga4Event,
  pushDataLayer,
  trackClick,
  trackEnquiry,
  trackGoal,
  trackInvalidEnquiryForm,
  trackLastMinuteDeal,
  trackServerSideEnquiryError,
  virtualPageView,
  DEFAULT_CONSENT,
  EVENT_TYPES,
  VIEWS
};



// WEBPACK FOOTER //
// ./shared/helpers/gtm_tracking.js